import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import DropTarget from '@uppy/drop-target'
import ThumbnailGenerator from '@uppy/thumbnail-generator'
import AwsS3, { type AwsBody } from '@uppy/aws-s3'
import { AwsClient } from 'aws4fetch'
import Uppy, { Meta, UppyFile } from '@uppy/core'
import { X, Paperclip } from 'lucide-react'
import { Button } from '@/components/ui/button.tsx'
import { createPortal } from 'react-dom'
import { createTemporaryCredentials } from '@/services/Firebase.ts'
import { useQuery } from '@tanstack/react-query'
import useStore from '@/stores/useStore.js'
import { nanoid } from 'nanoid'
import ALogoSpinner from '../atoms/ALogoSpinner.tsx'
import { cn } from '@/lib/utils.ts'
import { useToast } from '@/components/ui/use-toast.js'
import { RestrictionError } from '@uppy/core/lib/Restricter'

export const Form = ({
  organizationId,
  onUpload,
}: {
  organizationId: string
  onUpload: (assets: { name: string; url: string; type: string; key: string }[]) => void
}) => {
  const userId = useStore(state => state.user.id)
  const uppy = useUpload()
  const inputRef = useRef<HTMLInputElement>(null)

  useUploader(uppy, organizationId, userId)

  useEffect(() => {
    if (inputRef.current) {
      enrichUppy(uppy)
    }
  }, [inputRef.current, organizationId])

  useEffect(() => {
    uppy.on('restriction-failed', (_file, error) => {
      toast.toast({
        title: 'Could not upload your file',
        description: error.message,
        variant: 'destructive',
      })
    })
    uppy.on('complete', () => {
      const assets = uppy.getFiles().map(file => ({
        name: file.name ?? '',
        url: file.uploadURL ?? '',
        type: file.type!,
        key: file.meta?.key,
      }))
      onUpload(assets)
    })
    uppy.on('file-removed', () => {
      const assets = uppy.getFiles().map(file => ({
        name: file.name ?? '',
        url: file.uploadURL ?? '',
        type: file.type!,
        key: file.meta?.key,
      }))
      onUpload(assets)
    })
  }, [uppy])

  const toast = useToast()

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.currentTarget.files ?? [])

      files.forEach(file => {
        try {
          uppy.addFile({
            source: 'file input',
            name: file.name,
            type: file.type,
            data: file,
          })
        } catch (err) {
          if (err instanceof RestrictionError) {
            // pass, we handle it already
          } else {
            // handle other errors
            console.error(err)
          }
        }
      })
    },
    [uppy]
  )

  return (
    <div className="flex gap-2.5">
      <Button variant="ghost" size="icon" type="button" onClick={() => inputRef.current?.click()}>
        <Paperclip className="size-4" />
      </Button>
      <input
        type="file"
        ref={inputRef}
        id="upload-input"
        accept="image/*"
        className="sr-only"
        multiple={true}
        onChange={handleChange}
      />
    </div>
  )
}

const UploadContext = createContext<{
  uppy: UppyInstance
}>({
  get uppy(): Uppy<Meta & { key: string }, AwsBody> {
    throw new Error('uppy not initialized')
  },
})

export const Provider = ({
  children,
  organizationId,
}: {
  children: React.ReactNode
  organizationId: string
}) => {
  const [uppy, setUppy] = useState(() => createUppy(organizationId))

  useEffect(() => {
    setUppy(createUppy(organizationId))
  }, [organizationId])

  return <UploadContext.Provider value={{ uppy }}>{children}</UploadContext.Provider>
}

const useUpload = () => {
  const { uppy } = useContext(UploadContext)
  return uppy
}

export const Previews = () => {
  const uppy = useUpload()

  const state = useUppyState(uppy)

  return (
    <div className="flex flex-wrap gap-2.5">
      {Object.values(state.files).map(file => (
        <FilePreview key={file.id} file={file} onRemove={() => uppy.removeFile(file.id)}>
          {file.preview ? (
            <img
              src={file.preview}
              alt={file.name}
              height={64}
              width={64}
              className="size-16 rounded border border-border object-cover"
            />
          ) : (
            <div
              key={file.id}
              className="flex h-16 flex-col justify-center rounded border  border-border bg-secondary px-2 text-left text-secondary-foreground"
            >
              <span className="text-base font-medium">{file.name}</span>
              <span className="text-sm">
                {file.extension} ⋅ {((file.size ?? 0) / 1024).toFixed(1)} kB
              </span>
            </div>
          )}
        </FilePreview>
      ))}
    </div>
  )
}

function FilePreview({
  file,
  children,
  onRemove,
}: {
  file: UppyFile<Meta, AwsBody>
  children: React.ReactNode
  onRemove: () => void
}) {
  const { percentage, uploadComplete } = file.progress

  const skeleton = (
    <div className="flex size-16 items-center justify-center rounded border bg-base-background">
      <ALogoSpinner className="size-4 text-muted-foreground" />
    </div>
  )

  return (
    <div className="group relative" tabIndex={0}>
      <button
        type="button"
        className="absolute -right-1.5 -top-1.5 rounded-full border-2 border-white bg-secondary p-0.5 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100 focus-visible:opacity-100"
        onClick={onRemove}
      >
        <X className="size-3" strokeWidth={3} />
      </button>
      {!uploadComplete ? skeleton : children}
    </div>
  )
}

function createUppy(organizationId: string) {
  const uppy = new Uppy<Meta, AwsBody>({
    id: 'uppy',
    restrictions: {
      allowedFileTypes: ['image/*'],
      maxNumberOfFiles: 3,
      maxFileSize: 10_000_000,
    },
    autoProceed: true,
  })
  uppy.on('error', (file, error) => {
    console.error('error', file, error)
  })
  uppy.on('upload-error', (file, error) => {
    console.error('upload-error', file, error)
  })

  return uppy
}

function useUppyState(uppy: UppyInstance) {
  const [state, setState] = useState(uppy.getState())
  useEffect(() => {
    setState(uppy.getState())
    return uppy.store.subscribe(state => setState(state))
  }, [uppy])
  return state
}

function enrichUppy(uppy: UppyInstance) {
  if (uppy.getPlugin('ThumbnailGenerator')) {
    uppy.removePlugin(uppy.getPlugin('ThumbnailGenerator')!)
  }
  uppy.use(ThumbnailGenerator, {
    thumbnailHeight: 300,
    thumbnailWidth: 300,
  })

  if (uppy.getPlugin('DropTarget')) {
    uppy.removePlugin(uppy.getPlugin('DropTarget')!)
  }
  uppy.use(DropTarget, {
    id: 'DropTarget',
    target: document.body,
    onDragLeave(event) {
      uppy.getPlugin('DropTarget')!.setPluginState({
        isDraggingOver: false,
      })
    },
  })
}

function useUploader(uppy: UppyInstance, organizationId: string, userId: string) {
  const credentialsQuery = useQuery({
    queryKey: ['temporary-credentials', organizationId, userId],
    queryFn: async () => {
      const { data } = await createTemporaryCredentials({ organizationId })
      return data
    },
    staleTime: 60 * 60 * 1000, // 1 hour
  })

  useEffect(() => {
    if (!credentialsQuery.isSuccess) {
      return
    }
    const { endpoint, accessKeyId, secretAccessKey, sessionToken, bucketUrl } =
      credentialsQuery.data
    const r2 = new AwsClient({
      accessKeyId,
      secretAccessKey,
      sessionToken,
      region: 'auto',
      service: 's3',
    })

    if (uppy.getPlugin('s3')) {
      uppy.removePlugin(uppy.getPlugin('s3')!)
    }
    uppy.use(AwsS3, {
      id: 's3',
      endpoint,
      shouldUseMultipart: false,

      async getUploadParameters(file, opts) {
        const url = new URL(bucketUrl)
        url.pathname = [...url.pathname.split('/'), nanoid(24)].join('/')

        file.uploadURL = url.toString()
        uppy.setFileMeta(file.id, {
          uploadURL: url.toString(),
          key: url.pathname.startsWith('/') ? url.pathname.slice(1) : url.pathname,
        })

        url.searchParams.set('X-Amz-Expires', '604800')

        const signed = await r2.sign(
          new Request(url, {
            method: 'PUT',
          }),
          {
            aws: { signQuery: true },
          }
        )

        return {
          method: 'PUT',
          url: signed.url,
          headers: Object.fromEntries(signed.headers.entries()),
        }
      },
    })
  }, [uppy, credentialsQuery.data])
}

export function DropOverlay() {
  const uppy = useUpload()

  const state = useUppyState(uppy)
  const isDraggingOver = state.plugins.DropTarget?.isDraggingOver

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 flex items-center justify-center transition-all',
        isDraggingOver ? 'bg-white/50' : 'hidden'
      )}
    >
      <p className="px-5 py-10 text-center font-mono text-xl font-medium text-primary">
        Drop your files anywhere on this page.
        <br />
        You can upload up to 3 images.
      </p>
    </div>,
    document.body
  )
}

type UppyInstance = Uppy<Meta & { key: string }, AwsBody>
